import React, { ReactElement, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { getTextColor, getBorders, getTextType, getBackgroundTheme } from '../../../../../style';
import Container from '../../../../blocks/Container';
import OneTapOfferUnreadBadge from '../../OneTapOfferUnreadBadge';
import breakpoints from '../../../../../theme/material/breakpoints';
import { MEDIA_SCREEN_HEIGHT_ONE_TAP_SWIPE } from '../styles/media';
import { CustomStyledProps } from 'style/style';
import { isDarkMode } from 'utils/helpers';

const StyledContainer = styled(Container)({
  width: '100%',
  [breakpoints.up(769)]: {
    padding: '0 70px',
  },
});

const Nav = styled('div')(() => ({
  backgroundColor: getBackgroundTheme().personalInfo,
  borderBottom: getBorders().DEFAULT,
  borderStyle: 'solid',
  borderColor: 'transparent',
  borderWidth: 0,
  fontSize: 12,
  display: 'flex',
  listStyle: 'none',
  fontWeight: 'bold',
  ...getTextColor().DARK,
  [breakpoints.up(769)]: {
    backgroundColor: 'transparent',
  },
  [breakpoints.down('sm')]: {
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  },
}));

const List = styled('ul')({
  margin: '0',
  padding: '0',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexWrap: 'nowrap',
  width: '100%',
  listStyle: 'none',
  marginBottom: 10,
  [breakpoints.down('sm')]: {
    marginBottom: 0,
  },
});

const ListItem = styled('li')(
  () => ({
    fontWeight: 'bold',
    ...getTextColor().DARK,
    [breakpoints.up(769)]: {
      display: 'block',
    },
    display: 'inline-block',
    textAlign: 'center',
    position: 'relative',
  }),
  ({ isSelected }: CustomStyledProps) =>
    isSelected && {
      color: '#E21D4A',
    },
  ({ itemCount }: CustomStyledProps) =>
    itemCount && {
      width: `${100 / itemCount}%`,
    }
);

const CustomListItem = styled(ListItem)({
  [breakpoints.between(850, 1024)]: {
    marginTop: 14,
  },
  marginTop: 10,
  [breakpoints.between(641, 769)]: {
    marginTop: 14,
    position: 'absolute',
    right: 13,
  },
});

const StyledLink = styled(Link)(
  () => ({
    display: 'inline-block',
    padding: '22px 8px 8px 8px',
    whiteSpace: 'nowrap',
    ...getTextType().text,
    fontSize: 18,
    paddingBottom: 20,
    lineHeight: '20px',
    letterSpacing: ' -0.3px',
    color: isDarkMode() ? '#AFB0B1' : '#8F8F8F',
    [breakpoints.down('sm')]: {
      padding: 8,
      fontSize: 13,
      letterSpacing: ' -0.5px',
    },
    [breakpoints.down(360.05)]: {
      padding: 8,
      paddingBottom: 5,
    },
    [breakpoints.between(600, 1000)]: {
      [MEDIA_SCREEN_HEIGHT_ONE_TAP_SWIPE]: {
        paddingBottom: 5,
      },
    },
  }),
  ({ selected }: CustomStyledProps) =>
    selected && {
      fontWeight: 700,
      color: isDarkMode() ? '#FF3A67' : '#E21D4A',
    }
);

const BorderBottom = styled('div')(
  {
    borderRadius: 10,
    backgroundColor: 'transparent',
    height: 4,
    width: '100%',
    position: 'relative',
    marginTop: 8,
    [breakpoints.down(639.05)]: {
      marginTop: 4,
    },
  },
  ({ isSelected }: CustomStyledProps) =>
    isSelected && {
      backgroundColor: '#E21D4A',
      '::after': {
        content: '""',
        position: 'absolute',
        top: '-4px',
        left: 0,
        right: 0,
        margin: '0 auto',
        width: 0,
        height: 0,
        borderBottom: 'solid 4px #E21D4A',
        borderLeft: 'solid 4px transparent',
        borderRight: 'solid 4px transparent',
      },
    }
);

const LinkIconContianer = styled('span')`
  display: flex;
  align-items: center;
  gap: 5px;
  ${breakpoints.down(768.05)} {
    gap: 2px;
    & > svg {
      height: 11px;
    }
  }
`;

interface OneTapSwipeOfferTopNavProps {
  subRoutes: any;
  className: any;
  customItem: React.ReactNode;
  children?: React.ReactNode;
  themeDarkMode?: boolean;
}

const OneTapSwipeOfferTopNav = ({
  subRoutes,
  className,
  children,
  customItem,
}: OneTapSwipeOfferTopNavProps): ReactElement => {
  const location = useLocation();

  const buildMenu = useMemo(() => {
    const activePage = location.pathname;
    const itemCount = Object.keys(subRoutes).length;

    return subRoutes.map(routeItem => {
      const regex = routeItem.active;
      const active = regex.test(activePage);

      return (
        <ListItem itemCount={itemCount} key={routeItem.url} isSelected={active}>
          <StyledLink to={routeItem.url} selected={active}>
            <LinkIconContianer>
              {routeItem.url === '/offers/new' && <OneTapOfferUnreadBadge />}
              {active ? routeItem.icon.active : routeItem.icon.default} {routeItem.page}
            </LinkIconContianer>
            <BorderBottom isSelected={active} />
          </StyledLink>
        </ListItem>
      );
    });
  }, [location.pathname, subRoutes]);

  return (
    <Nav className={className} data-test-id={className}>
      <StyledContainer fullWidth>
        <List id="top-nav-list">
          {buildMenu}
          {customItem && <CustomListItem>{customItem}</CustomListItem>}
        </List>
      </StyledContainer>
      {children}
    </Nav>
  );
};

export default React.memo(OneTapSwipeOfferTopNav);
